/**
 * Gemini AI Service - Proxies requests to backend API
 */
import apiClient from '../../api/apiClient';

const generateGemini = async (prompt, retries = 3) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await apiClient.post('/ai/generate', { prompt });
      return response.data.text;
    } catch (error) {
      const isGoogleBusy = error.response?.status === 503;
      const isNetworkError = !error.response;
      
      // Retry ONLY if it's a 503 or a pure network error.
      // Do NOT retry for 4xx or 500 errors from our own backend.
      if (attempt === retries || (!isGoogleBusy && !isNetworkError)) {
        console.error("AI Generation Error:", error.response?.data || error.message);
        throw new Error(error.response?.data?.error?.message || error.response?.data?.message || error.message || "Failed to generate AI content.");
      }
      
      console.warn(`AI Generation failed. Retrying in ${attempt * 2} seconds... (Attempt ${attempt}/${retries})`);
      await new Promise(resolve => setTimeout(resolve, attempt * 2000));
    }
  }
};

/**
 * Generates a hotel overview
 */
export const generateText = async (hotelName, location, packagePrice) => {
  const prompt = `Write an engaging and professional hotel overview for a travel website.
    Hotel Name: "${hotelName}"
    Location: ${location}
    Starting Package Price: ₹${packagePrice}
    The overview should be approximately 400 words.`;

  return await generateGemini(prompt);
};

/**
 * Generates a short description
 */
export const generateDescription = async (hotelName, location) => {
  const prompt = `Write a short, catchy 20-word summary for "${hotelName}" located in ${location}.`;
  return await generateGemini(prompt);
};

/**
 * Generates a vendor company description for the public profile.
 */
export const generateCompanyDescription = async (companyName, categories, location) => {
  const services = Array.isArray(categories) && categories.length
    ? categories.join(', ')
    : 'travel and hospitality services';
  const prompt = `Write an engaging and professional About Company description for a travel marketplace vendor.
    Company Name: "${companyName}"
    Services: ${services}
    Location: ${location || 'India'}
    Write approximately 120-180 words in 2 short paragraphs. Highlight trust, customer service, and the listed services without inventing awards, years of experience, ratings, or facts that were not provided. Do NOT use markdown, headings, bullet points, or quotation marks. Return only the description.`;

  return await generateGemini(prompt);
};

// Compatibility aliases
export const generateOverview = generateText;

export const generatePackageDescription = async (packageName, location, nights, days, packagePrice) => {
  const prompt = `Write a short, engaging description (around 30-50 words) for a travel package named "${packageName}" in ${location} for ${nights} nights and ${days} days, priced at ₹${packagePrice}. Do NOT use any markdown formatting (like ** for bold). Provide only plain text.`;
  return await generateGemini(prompt);
};

export const generatePackageOverview = async (packageName, location, nights, days, packagePrice) => {
  const prompt = `Write a comprehensive and engaging travel itinerary overview for "${packageName}" in ${location} for ${nights} nights and ${days} days, priced at ₹${packagePrice}. Provide the response in clean HTML format suitable for a rich text editor. Use standard HTML tags like <h3>, <p>, <ul>, <li>, and <strong>. Do NOT wrap the response in markdown code blocks.`;
  return await generateGemini(prompt);
};
